import styled from '@emotion/styled';
import { Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../../entity/ProductPricing';
import { ProjectType } from '../../../../generated/graphql';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';
import { InputCreationSetAmount } from '../InputCreationSetAmount';

type Props = {
  isPointRequired: boolean;
  projectType: ProjectType;
  productPricing?: ProductPricing;
  subTotal?: number;
  requiredPoint?: number;
  expressDuePointTotal?: number;
  optionPointTotal?: number;
  orderTotal: number;
  creationSetAmount: number;
};

const CreationSetSubTotal: FC<Props> = ({
  isPointRequired,
  projectType,
  productPricing,
  subTotal,
  requiredPoint,
  expressDuePointTotal,
  optionPointTotal,
  orderTotal,
  creationSetAmount,
}) => {
  return (
    <Root>
      <Table>
        <TableBody>
          {isPointRequired && productPricing ? (
            <TableRow>
              <DenserTableCellWithoutBorderTitle align="right">
                <Title variant="subtitle1">
                  <FormattedMessage id="project_editing_page.for_preview.section_heading.required_point.label" />
                </Title>
              </DenserTableCellWithoutBorderTitle>

              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint>
                    {productPricing.currency}
                    {((requiredPoint || 0) * productPricing.flatRate).toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            </TableRow>
          ) : null}
          {isPointRequired && productPricing && expressDuePointTotal !== 0 ? (
            <TableRow>
              <DenserTableCellWithoutBorderTitle align="right">
                <Title variant="subtitle1">
                  <FormattedMessage id="project_editing_page.for_preview.section_heading.express_due.label" />
                </Title>
              </DenserTableCellWithoutBorderTitle>

              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint>
                    {productPricing.currency}
                    {((expressDuePointTotal || 0) * productPricing.flatRate).toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            </TableRow>
          ) : null}
          {isPointRequired && productPricing && optionPointTotal !== 0 ? (
            <TableRow>
              <DenserTableCellWithoutBorderTitle align="right">
                <Title variant="subtitle1">
                  <FormattedMessage id="project_editing_page.for_preview.section_heading.option_merchandise.label" />
                </Title>
              </DenserTableCellWithoutBorderTitle>

              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint>
                    {productPricing.currency}
                    {((optionPointTotal || 0) * productPricing.flatRate).toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            </TableRow>
          ) : null}
          <TableRow>
            <DenserTableCellWithoutBorderTitle align="right">
              <Title variant="subtitle1">
                <FormattedMessage id="project_editing_page.for_preview.section_heading.creation_set_amount.label" />
              </Title>
            </DenserTableCellWithoutBorderTitle>
            <DenserTableCellWithoutBorder>
              <AlignRight>
                <InputCreationSetAmount creationSetAmount={creationSetAmount} projectType={projectType} />
              </AlignRight>
            </DenserTableCellWithoutBorder>
          </TableRow>
          <TableRow>
            <DenserTableCellWithoutBorderTitle align="right">
              <Title variant="subtitle1">
                <FormattedMessage id="project_editing_page.for_preview.section_heading.order_count.label" />
              </Title>
            </DenserTableCellWithoutBorderTitle>
            <DenserTableCellWithoutBorder>
              <AlignRight>
                <RequiredPoint>{orderTotal}</RequiredPoint>
              </AlignRight>
            </DenserTableCellWithoutBorder>
          </TableRow>
          {isPointRequired && productPricing ? (
            <TableRow>
              <DenserTableCellWithoutBorderTitle align="right">
                <Title variant="subtitle1">
                  <FormattedMessage id="project_editing_page.for_preview.section_heading.sub_total.label" />
                </Title>
              </DenserTableCellWithoutBorderTitle>
              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint>
                    {productPricing.currency}
                    {((subTotal || 0) * creationSetAmount * productPricing.flatRate).toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Root>
  );
};

const Root = styled('div')({});

const DenserTableCell = styled(TableCell)();
DenserTableCell.defaultProps = { size: 'small' };

const DenserTableCellWithoutBorder = styled(DenserTableCell)({
  borderBottom: 0,
  [mq('sp')]: {
    padding: '6px 24px 6px 0px',
  },
});

const DenserTableCellWithoutBorderTitle = styled(DenserTableCell)({
  borderBottom: 0,
  [mq('sp')]: {
    padding: '6px 2px',
  },
});

const Title = styled(Typography)({
  [mq('sp')]: {
    fontSize: '0.8rem',
  },
});

const AlignRight = styled('div')(
  flexibleLayoutContainer({
    justifyContent: 'flex-end',
  }),
);

const RequiredPoint = styled(Typography)({});
RequiredPoint.defaultProps = { variant: 'subtitle1' };

export default CreationSetSubTotal;
